import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/auth/request-password-reset", { email });
        } catch (_) {
            // Intentionally swallow — always show the same confirmation.
        } finally {
            setLoading(false);
            setSent(true);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
            <div className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-10">
                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                </Link>

                {sent ? (
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <div className="h-16 w-16 rounded-2xl bg-brand-gradient flex items-center justify-center shadow-mx-md">
                                <MailCheck className="h-8 w-8 text-white" />
                            </div>
                        </div>
                        <h1 className="font-display text-3xl tracking-tight">Check your inbox</h1>
                        <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">
                            If an account exists for <span className="text-foreground font-medium">{email}</span>,
                            a password reset link has been sent. It expires in 60 minutes.
                        </p>
                        <p className="text-xs text-muted-foreground pt-2">
                            Didn't get it? Check your spam folder, or{" "}
                            <button onClick={() => setSent(false)} className="text-foreground underline underline-offset-2">
                                try again
                            </button>.
                        </p>
                    </div>
                ) : (
                    <>
                        <h1 className="font-display text-4xl tracking-tight leading-tight">Reset password</h1>
                        <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                            Enter your work email. If we find a matching account, we'll send a reset link.
                        </p>

                        <form onSubmit={onSubmit} className="mt-8 space-y-5">
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-widest font-bold">Email</Label>
                                <Input
                                    id="email" type="email" required autoFocus
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@mxacademy.in"
                                    className="mt-2 h-12 rounded-xl"
                                />
                            </div>
                            <Button type="submit" disabled={loading} className="mx-btn-primary h-12 w-full text-base">
                                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
